import * as React from 'react'
import clsx from 'clsx'
import { useForkRef, Tooltip, TooltipProps } from '@applift/factor'
import { ReactTableSortDirection } from '../../models/gridReactTable'
import { GridColumnHeaderTitle } from './GridColumnHeaderTitle'
import {
  GridColumnHeaderSeparator,
  GridColumnHeaderSeparatorProps,
} from './GridColumnHeaderSeparator'
import { useGridPrivateApiContext } from '../../hooks/utils/useGridPrivateApiContext'

interface GridGenericColumnHeaderItemProps {
  classes: Record<
    'root' | 'draggableContainer' | 'titleContainer' | 'titleContainerContent',
    string
  >
  colIndex: number
  height: number
  isResizing: boolean
  resizable: boolean
  sortDirection: false | ReactTableSortDirection
  headerComponent?: React.ReactNode
  headerClassName?: string
  isDraggable: boolean
  hasFocus?: boolean
  tabIndex: 0 | -1
  width: number
  label: string
  description?: TooltipProps['title']
  descriptionProps?: Omit<TooltipProps, 'title'>
  draggableContainerProps?: Partial<React.HTMLProps<HTMLDivElement>>
  columnHeaderSeparatorProps?: Partial<GridColumnHeaderSeparatorProps>
  disableHeaderSeparator?: boolean
  columnTitleIconButtons?: React.ReactNode
  disableColumnResize?: boolean
  separatorSide?: GridColumnHeaderSeparatorProps['side']
}

export const GridGenericColumnHeaderItem = React.forwardRef(
  function GridGenericColumnHeaderItem(
    props: GridGenericColumnHeaderItemProps,
    ref
  ) {
    const {
      classes,
      colIndex,
      height,
      isResizing,
      sortDirection,
      hasFocus,
      tabIndex = -1,
      isDraggable,
      headerComponent,
      width,
      label,
      description,
      descriptionProps,
      resizable,
      draggableContainerProps,
      columnHeaderSeparatorProps,
      disableHeaderSeparator,
      separatorSide,
      columnTitleIconButtons = null,
      ...other
    } = props

    const apiRef = useGridPrivateApiContext()

    const headerCellRef = React.useRef<HTMLDivElement>(null)
    const handleRef = useForkRef(headerCellRef, ref)

    let ariaSort: 'ascending' | 'descending' | 'none' = 'none'
    if (sortDirection !== false) {
      ariaSort = sortDirection === 'asc' ? 'ascending' : 'descending'
    }

    React.useLayoutEffect(() => {
      if (hasFocus) {
        const focusableElement =
          headerCellRef.current?.querySelector<HTMLElement>('[tabindex="0"]')
        const elementToFocus = focusableElement || headerCellRef.current
        elementToFocus?.focus()
        apiRef.current.columnHeadersContainerElementRef!.current!.scrollLeft = 0
      }
    }, [apiRef, hasFocus])

    return (
      <Tooltip title={description} arrow placement="top" {...descriptionProps}>
        <div
          ref={handleRef}
          className={clsx(classes.root)}
          style={{
            height,
            width,
            minWidth: width,
            maxWidth: width,
          }}
          tabIndex={tabIndex}
          role="columnheader"
          aria-colindex={colIndex + 1}
          aria-sort={ariaSort}
          aria-label={headerComponent == null ? label : undefined}
          {...other}
        >
          <div
            className={clsx(classes.draggableContainer)}
            draggable={isDraggable}
            {...draggableContainerProps}
          >
            <div className={clsx(classes.titleContainer)}>
              <div className={clsx(classes.titleContainerContent)}>
                {headerComponent !== undefined ? (
                  <GridColumnHeaderTitle
                    // @ts-ignore
                    label={headerComponent}
                    columnWidth={width}
                  />
                ) : (
                  <GridColumnHeaderTitle label={label} columnWidth={width} />
                )}
              </div>
              {columnTitleIconButtons}
            </div>
          </div>
          {!disableHeaderSeparator && (
            <GridColumnHeaderSeparator
              resizable={!!resizable}
              resizing={isResizing}
              height={height}
              side={separatorSide}
              {...columnHeaderSeparatorProps}
            />
          )}
        </div>
      </Tooltip>
    )
  }
)
